import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import MainLayout from '../../components/layout/MainLayout';
import Avatar from '../../components/common/Avatar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Euro, Search, Eye, FileText, User, Calendar, Clock,
  CheckCircle, Loader2, Users, Briefcase, HandCoins, 
  ThumbsUp, XCircle, ChevronDown, ChevronRight, CreditCard, 
  Banknote, Building2, Wallet, TrendingUp, TrendingDown, 
  ArrowRight, Send, Archive, FileCheck, Timer
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';

import { useNavigate } from 'react-router-dom';

// Using api from lib/axios

// Payment request status config
const STATUS_CONFIG = {
  validated: { 
    label: 'Validé', 
    color: 'bg-blue-100 text-blue-700', 
    icon: CheckCircle,
    description: 'Rapport validé, prêt pour mise en paiement'
  },
  pending_payment: { 
    label: 'À payer', 
    color: 'bg-amber-100 text-amber-700', 
    icon: Clock,
    description: 'En attente de paiement'
  },
  paid: { 
    label: 'Payé', 
    color: 'bg-emerald-100 text-emerald-700', 
    icon: CheckCircle,
    description: 'Paiement effectué'
  },
};

const PAYMENT_METHODS = [
  { value: 'bank_transfer', label: 'Virement bancaire', icon: Building2 },
  { value: 'check', label: 'Chèque', icon: FileText },
  { value: 'paypal', label: 'PayPal', icon: Wallet },
  { value: 'cash', label: 'Espèces', icon: Banknote },
  { value: 'other', label: 'Autre', icon: CreditCard },
];

const PaymentRequestsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('creatives');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Data
  const [creativeRequests, setCreativeRequests] = useState([]);
  const [affiliateRequests, setAffiliateRequests] = useState([]);
  const [stats, setStats] = useState({});
  
  // UI State
  const [expandedReport, setExpandedReport] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    payment_method: 'bank_transfer',
    payment_date: new Date().toISOString().split('T')[0],
    payment_reference: '',
    notes: ''
  });

  useEffect(() => {
    fetchPaymentRequests();
  }, []);

  const fetchPaymentRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/payment-requests');
      setCreativeRequests(response.data.creatives || []);
      setAffiliateRequests(response.data.affiliates || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching payment requests:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPendingPayment = async (request) => {
    setActionLoading(true);
    try {
      await api.post(`/payment-requests/${request.id}/mark-pending-payment`);
      toast.success('Rapport marqué "À payer" - Notification envoyée au membre');
      fetchPaymentRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkAsPaid = async () => {
    if (!selectedRequest) return;
    if (!paymentForm.payment_method) {
      toast.error('Veuillez sélectionner un mode de paiement');
      return;
    }
    
    setActionLoading(true);
    try {
      await api.post(`/payment-requests/${selectedRequest.id}/mark-paid`, {
        payment_method: paymentForm.payment_method,
        payment_date: paymentForm.payment_date,
        payment_reference: paymentForm.payment_reference,
        notes: paymentForm.notes
      });
      toast.success('Paiement enregistré - Notification envoyée au membre');
      setShowPaymentModal(false);
      setSelectedRequest(null);
      setPaymentForm({
        payment_method: 'bank_transfer',
        payment_date: new Date().toISOString().split('T')[0],
        payment_reference: '',
        notes: ''
      });
      fetchPaymentRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setActionLoading(false);
    }
  };

  const openPaymentModal = (request) => {
    setSelectedRequest(request);
    setShowPaymentModal(true);
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}min`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h${mins}min`;
  };

  const filterRequests = (requests) => {
    return requests.filter(r => {
      const matchesStatus = filterStatus === 'all' || r.status === filterStatus;
      const matchesSearch = !searchQuery || 
        r.member_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.affiliate_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.month_label?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  };

  const filteredCreatives = filterRequests(creativeRequests);
  const filteredAffiliates = filterRequests(affiliateRequests);

  // Render report card
  const renderReportCard = (request) => {
    const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.validated;
    const StatusIcon = statusConfig.icon;
    const isExpanded = expandedReport === request.id;
    const hasBonusMalus = request.total_bonus_malus && request.total_bonus_malus !== 0;
    
    return (
      <Card key={request.id} className="overflow-hidden">
        {/* Header - Clickable */}
        <div 
          className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => setExpandedReport(isExpanded ? null : request.id)}
        >
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <Avatar 
              name={request.member_name} 
              src={request.member_avatar}
              size="md"
            />
            
            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (request.member_id) navigate(`/team/${request.member_id}`);
                  }}
                  className="font-semibold hover:text-[#00d4aa] hover:underline transition-colors"
                >
                  {request.member_name}
                </button>
                <Badge variant="outline" className="text-[10px]">
                  {request.member_type === 'external' ? 'Externe' : 'Interne'}
                </Badge>
              </div>
              <p className="text-sm text-slate-500">
                {request.month_label} • {request.total_hours?.toFixed(1) || 0}h • {request.tasks_count || 0} tâches
              </p>
            </div>
            
            {/* Amount */}
            <div className="text-right">
              {hasBonusMalus && (
                <div className="flex items-center justify-end gap-1 text-xs mb-0.5">
                  <span className="text-slate-400 line-through">{request.base_amount?.toFixed(0)}€</span>
                  {request.total_bonus_malus > 0 ? (
                    <TrendingUp className="w-3 h-3 text-green-500" />
                  ) : (
                    <TrendingDown className="w-3 h-3 text-red-500" />
                  )}
                </div>
              )}
              <p className="text-xl font-bold" style={{ color: theme.colors?.primary }}>
                {(request.final_amount || 0).toLocaleString('fr-FR')}€
              </p>
            </div>
            
            {/* Status */}
            <Badge className={cn("text-xs shrink-0", statusConfig.color)}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusConfig.label}
            </Badge>
            
            {/* Actions */}
            <div className="flex items-center gap-2 shrink-0">
              {request.status === 'validated' && (
                <Button
                  size="sm"
                  className="h-8 text-xs gap-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMarkPendingPayment(request);
                  }}
                  disabled={actionLoading}
                  style={{ backgroundColor: theme.colors?.accent }}
                >
                  <Send className="w-3 h-3" />
                  Mettre à payer
                </Button>
              )}
              {request.status === 'pending_payment' && (
                <Button
                  size="sm"
                  className="h-8 text-xs gap-1 bg-green-600 hover:bg-green-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    openPaymentModal(request);
                  }}
                >
                  <Euro className="w-3 h-3" />
                  Payer
                </Button>
              )}
              {request.status === 'paid' && (
                <div className="text-xs text-slate-500 text-right">
                  <p>Payé le {new Date(request.payment_date || request.paid_at).toLocaleDateString('fr-FR')}</p>
                  <p className="text-[10px]">{PAYMENT_METHODS.find(m => m.value === request.payment_method)?.label || request.payment_method}</p>
                </div>
              )}
              
              {/* Expand icon */}
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-slate-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-slate-400" />
              )}
            </div>
          </div>
        </div>
        
        {/* Expanded details */}
        {isExpanded && (
          <div className="border-t bg-slate-50 p-4">
            {/* Summary stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-white p-3 rounded-lg border">
                <p className="text-xs text-slate-500">Heures totales</p>
                <p className="text-lg font-bold">{request.total_hours?.toFixed(1) || 0}h</p>
              </div>
              <div className="bg-white p-3 rounded-lg border">
                <p className="text-xs text-slate-500">Taux horaire</p>
                <p className="text-lg font-bold">{request.hourly_rate || 0}€/h</p>
              </div>
              <div className="bg-white p-3 rounded-lg border">
                <p className="text-xs text-slate-500">Montant base</p>
                <p className="text-lg font-bold">{(request.base_amount || 0).toFixed(0)}€</p>
              </div>
              <div className="bg-white p-3 rounded-lg border">
                <p className="text-xs text-slate-500">Bonus/Malus</p>
                <p className={cn(
                  "text-lg font-bold",
                  request.total_bonus_malus > 0 ? "text-green-600" : request.total_bonus_malus < 0 ? "text-red-600" : ""
                )}>
                  {request.total_bonus_malus > 0 ? '+' : ''}{(request.total_bonus_malus || 0).toFixed(0)}€
                </p>
              </div>
            </div>
            
            {/* Tasks table */}
            {request.tasks && request.tasks.length > 0 && (
              <div>
                <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Détail des tâches ({request.tasks.length})
                </h4>
                <div className="bg-white rounded-lg border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs">Tâche</TableHead>
                        <TableHead className="text-xs">Projet</TableHead>
                        <TableHead className="text-xs text-center">Durée</TableHead>
                        <TableHead className="text-xs text-right">Montant</TableHead>
                        <TableHead className="text-xs text-right">Bonus/Malus</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {request.tasks.map((task, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-sm font-medium">
                            {task.task_name || task.title}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {task.project_name || '-'}
                          </TableCell>
                          <TableCell className="text-sm text-center">
                            {formatDuration(task.consumed_minutes || task.duration_minutes)}
                          </TableCell>
                          <TableCell className="text-sm text-right font-mono">
                            {(task.amount || 0).toFixed(0)}€
                          </TableCell>
                          <TableCell className="text-sm text-right">
                            {task.bonus_malus ? (
                              <span className={cn(
                                "font-medium",
                                task.bonus_malus > 0 ? "text-green-600" : "text-red-600"
                              )}>
                                {task.bonus_malus > 0 ? '+' : ''}{task.bonus_malus}€
                              </span>
                            ) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
            
            {/* Agency notes */}
            {request.agency_notes && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs font-medium text-blue-700 mb-1">Note de l'agence</p>
                <p className="text-sm text-blue-800">{request.agency_notes}</p>
              </div>
            )}
            
            {/* Validation info */}
            <div className="mt-4 pt-3 border-t flex flex-wrap gap-4 text-xs text-slate-500">
              {request.validated_at && (
                <span>Validé le {new Date(request.validated_at).toLocaleDateString('fr-FR')} par {request.validated_by_name}</span>
              )}
              {request.pending_payment_at && (
                <span>Mis en paiement le {new Date(request.pending_payment_at).toLocaleDateString('fr-FR')}</span>
              )}
              {request.paid_at && (
                <span className="text-green-600">Payé le {new Date(request.paid_at).toLocaleDateString('fr-FR')}</span>
              )}
            </div>
          </div>
        )}
      </Card>
    );
  };

  // Render affiliate card - with expandable details like report cards
  const renderAffiliateCard = (request) => {
    const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.validated;
    const StatusIcon = statusConfig.icon;
    const isExpanded = expandedReport === request.id;
    
    return (
      <Card key={request.id} className="overflow-hidden">
        {/* Header - Clickable */}
        <div 
          className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => setExpandedReport(isExpanded ? null : request.id)}
        >
          <div className="flex items-center gap-4">
            {/* Icon */}
            <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
              <HandCoins className="w-6 h-6 text-orange-600" />
            </div>
            
            {/* Info */}
            <div className="flex-1 min-w-0">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (request.affiliate_id) navigate(`/affiliates/${request.affiliate_id}`);
                }}
                className="font-semibold hover:text-[#00d4aa] hover:underline transition-colors"
              >
                {request.affiliate_name}
              </button>
              <p className="text-sm text-slate-500">
                {request.leads_converted} lead(s) converti(s) • {request.commission_rate}% commission
              </p>
              <p className="text-xs text-slate-400">Période: {request.period}</p>
            </div>
            
            {/* Amount */}
            <div className="text-right">
              <p className="text-xs text-slate-400">CA généré: {(request.total_sales || 0).toLocaleString('fr-FR')}€</p>
              <p className="text-xl font-bold text-orange-600">
                {(request.final_amount || request.commission_amount || 0).toLocaleString('fr-FR')}€
              </p>
            </div>
            
            {/* Status */}
            <Badge className={cn("text-xs", statusConfig.color)}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusConfig.label}
            </Badge>
            
            {/* Actions */}
            <div className="flex items-center gap-2 shrink-0">
              {request.status === 'validated' && (
                <Button
                  size="sm"
                  className="h-8 text-xs gap-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMarkPendingPayment(request);
                  }}
                  disabled={actionLoading}
                  style={{ backgroundColor: theme.colors?.accent }}
                >
                  <Send className="w-3 h-3" />
                  À payer
                </Button>
              )}
              {request.status === 'pending_payment' && (
                <Button
                  size="sm"
                  className="h-8 text-xs gap-1 bg-green-600 hover:bg-green-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    openPaymentModal(request);
                  }}
                >
                  <Euro className="w-3 h-3" />
                  Payer
                </Button>
              )}
              {request.status === 'paid' && (
                <div className="text-xs text-slate-500 text-right">
                  <p>Payé le {new Date(request.payment_date || request.paid_at).toLocaleDateString('fr-FR')}</p>
                  <p className="text-[10px]">{PAYMENT_METHODS.find(m => m.value === request.payment_method)?.label || request.payment_method}</p>
                </div>
              )}
              
              {/* Expand icon */}
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-slate-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-slate-400" />
              )}
            </div>
          </div>
        </div>
        
        {/* Expanded details - Shows leads converted */}
        {isExpanded && (
          <div className="border-t bg-slate-50 p-4">
            {/* Summary stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-white p-3 rounded-lg border">
                <p className="text-xs text-slate-500">Leads convertis</p>
                <p className="text-lg font-bold text-orange-600">{request.leads_converted || 0}</p>
              </div>
              <div className="bg-white p-3 rounded-lg border">
                <p className="text-xs text-slate-500">CA généré</p>
                <p className="text-lg font-bold">{(request.total_sales || 0).toLocaleString('fr-FR')}€</p>
              </div>
              <div className="bg-white p-3 rounded-lg border">
                <p className="text-xs text-slate-500">Taux commission</p>
                <p className="text-lg font-bold">{request.commission_rate || 10}%</p>
              </div>
              <div className="bg-white p-3 rounded-lg border">
                <p className="text-xs text-slate-500">Commission nette</p>
                <p className="text-lg font-bold text-green-600">{(request.final_amount || request.commission_amount || 0).toLocaleString('fr-FR')}€</p>
              </div>
            </div>
            
            {/* Leads table */}
            {request.leads && request.leads.length > 0 ? (
              <div>
                <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Leads convertis ({request.leads.length})
                </h4>
                <div className="bg-white rounded-lg border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs">Client</TableHead>
                        <TableHead className="text-xs">Projet</TableHead>
                        <TableHead className="text-xs text-center">Date conversion</TableHead>
                        <TableHead className="text-xs text-right">Montant projet</TableHead>
                        <TableHead className="text-xs text-right">Commission</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {request.leads.map((lead, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-sm">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                                <User className="w-4 h-4 text-orange-600" />
                              </div>
                              <div>
                                {lead.client_id ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/clients/${lead.client_id}`);
                                    }}
                                    className="font-medium hover:text-[#00d4aa] hover:underline transition-colors"
                                  >
                                    {lead.client_name || lead.company_name || 'Client'}
                                  </button>
                                ) : (
                                  <p className="font-medium">{lead.client_name || lead.company_name || 'Client'}</p>
                                )}
                                <p className="text-xs text-slate-400">{lead.client_email || ''}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {lead.project_id ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/projects/${lead.project_id}`);
                                }}
                                className="hover:text-[#00d4aa] hover:underline transition-colors"
                              >
                                {lead.project_name || lead.project_title || '-'}
                              </button>
                            ) : (
                              lead.project_name || lead.project_title || '-'
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-center">
                            {lead.converted_at ? new Date(lead.converted_at).toLocaleDateString('fr-FR') : '-'}
                          </TableCell>
                          <TableCell className="text-sm text-right font-mono">
                            {(lead.project_amount || lead.amount || 0).toLocaleString('fr-FR')}€
                          </TableCell>
                          <TableCell className="text-sm text-right">
                            <span className="font-medium text-green-600">
                              +{(lead.commission || (lead.project_amount || 0) * (request.commission_rate || 10) / 100).toLocaleString('fr-FR')}€
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500 bg-white rounded-lg border">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Détails des leads non disponibles</p>
                <p className="text-xs text-slate-400">Les informations détaillées seront visibles lors de la prochaine période</p>
              </div>
            )}
            
            {/* Validation info */}
            <div className="mt-4 pt-3 border-t flex flex-wrap gap-4 text-xs text-slate-500">
              {request.validated_at && (
                <span>Validé le {new Date(request.validated_at).toLocaleDateString('fr-FR')}</span>
              )}
              {request.pending_payment_at && (
                <span>Mis en paiement le {new Date(request.pending_payment_at).toLocaleDateString('fr-FR')}</span>
              )}
              {request.paid_at && (
                <span className="text-green-600">Payé le {new Date(request.paid_at).toLocaleDateString('fr-FR')}</span>
              )}
            </div>
          </div>
        )}
      </Card>
    );
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
            Règlements à effectuer
          </h1>
          <p className="text-slate-500">
            Gérez les paiements des rapports validés pour les membres et apporteurs d'affaires
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <TabsList className="bg-white border">
              <TabsTrigger value="creatives" className="gap-2">
                <User className="w-4 h-4" />
                Rapports membres
                {(stats.creatives?.validated || 0) + (stats.creatives?.pending_payment || 0) > 0 && (
                  <Badge variant="destructive" className="ml-1 h-5 px-1.5 text-xs">
                    {(stats.creatives?.validated || 0) + (stats.creatives?.pending_payment || 0)}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="affiliates" className="gap-2">
                <HandCoins className="w-4 h-4" />
                Apporteurs
                {(stats.affiliates?.validated || 0) + (stats.affiliates?.pending_payment || 0) > 0 && (
                  <Badge variant="destructive" className="ml-1 h-5 px-1.5 text-xs">
                    {(stats.affiliates?.validated || 0) + (stats.affiliates?.pending_payment || 0)}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
            
            {/* Filters */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-48"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="validated">Validés</SelectItem>
                  <SelectItem value="pending_payment">À payer</SelectItem>
                  <SelectItem value="paid">Payés</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <FileCheck className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {activeTab === 'creatives' ? stats.creatives?.validated || 0 : stats.affiliates?.validated || 0}
                    </p>
                    <p className="text-xs text-slate-500">Validés</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-700">
                      {activeTab === 'creatives' ? stats.creatives?.pending_payment || 0 : stats.affiliates?.pending_payment || 0}
                    </p>
                    <p className="text-xs text-amber-600">À payer</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-600">
                      {activeTab === 'creatives' ? stats.creatives?.paid || 0 : stats.affiliates?.paid || 0}
                    </p>
                    <p className="text-xs text-slate-500">Payés</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${theme.colors?.accent}20` }}>
                    <Euro className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold" style={{ color: theme.colors?.accent }}>
                      {(activeTab === 'creatives' 
                        ? (stats.creatives?.pending_amount || 0) 
                        : (stats.affiliates?.total_amount || 0)
                      ).toLocaleString('fr-FR')}€
                    </p>
                    <p className="text-xs text-slate-500">À régler</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Content */}
          <TabsContent value="creatives" className="mt-6">
            {filteredCreatives.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">Aucun rapport à afficher</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Les rapports validés apparaîtront ici pour mise en paiement
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredCreatives.map(request => renderReportCard(request))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="affiliates" className="mt-6">
            {filteredAffiliates.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <HandCoins className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">Aucun rapport d'apporteur</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Les commissions des apporteurs d'affaires apparaîtront ici
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredAffiliates.map(request => renderAffiliateCard(request))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Payment Modal */}
        <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ color: theme.colors?.primary }}>
                Enregistrer le paiement
              </DialogTitle>
            </DialogHeader>
            
            {selectedRequest && (
              <div className="space-y-4">
                {/* Summary */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <Avatar name={selectedRequest.member_name || selectedRequest.affiliate_name} size="sm" />
                    <div>
                      <p className="font-medium">{selectedRequest.member_name || selectedRequest.affiliate_name}</p>
                      <p className="text-sm text-slate-500">{selectedRequest.month_label || selectedRequest.period}</p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t">
                    <span className="text-slate-600">Montant à payer</span>
                    <span className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                      {(selectedRequest.final_amount || selectedRequest.commission_amount || 0).toLocaleString('fr-FR')}€
                    </span>
                  </div>
                </div>
                
                {/* Payment form */}
                <div className="space-y-4">
                  <div>
                    <Label>Mode de paiement *</Label>
                    <Select 
                      value={paymentForm.payment_method} 
                      onValueChange={(v) => setPaymentForm(prev => ({ ...prev, payment_method: v }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_METHODS.map(method => (
                          <SelectItem key={method.value} value={method.value}>
                            <div className="flex items-center gap-2">
                              <method.icon className="w-4 h-4" />
                              {method.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Date du paiement *</Label>
                    <Input
                      type="date"
                      value={paymentForm.payment_date}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_date: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label>Référence (optionnel)</Label>
                    <Input
                      placeholder="N° de virement, chèque..."
                      value={paymentForm.payment_reference}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_reference: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label>Note (optionnel)</Label>
                    <Textarea
                      placeholder="Commentaire interne..."
                      value={paymentForm.notes}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPaymentModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleMarkAsPaid}
                disabled={actionLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Confirmer le paiement
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default PaymentRequestsPage;
